import { useAuthStore } from '@/stores/auth'
import { useUserStore } from '@/stores/user'
import { usePermissionsStore } from '@/stores/permissions'
import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { isTokenBasedGuestRoute } from '@/utils/public-auth-routes'
import { useToast } from 'vue-toastification'
import i18n from '@/config/i18n'
import { PERMISSIONS } from '@/utils/permissions'
import { resolveDefaultLandingRoute } from '@/utils/defaultLandingRoute'

function notifyRouteForbidden(): void {
  useToast().error(i18n.global.t('errors.forbidden'))
}

export const authGuard = async (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const authStore = useAuthStore()
  const userStore = useUserStore()
  const permissionsStore = usePermissionsStore()

  if (to.meta.requiresAuth) {
    if (!authStore.bootstrapped) {
      await authStore.bootstrap()
    }

    if (!authStore.isAuthenticated) {
      next({
        path: '/auth/login',
        query: { redirect: to.fullPath }
      })
      return
    }

    if (!authStore.user) {
      await authStore.checkAuth()
    }

    if (authStore.isAuthenticated && !userStore.profile) {
      await userStore.fetchProfile()
    }

    if (authStore.isAuthenticated && permissionsStore.permissions.length === 0 && !permissionsStore.loading) {
      await permissionsStore.fetchPermissions({ force: true })
    }

    if (
      to.name === 'Dashboard' &&
      !authStore.isSuperAdmin &&
      !permissionsStore.hasPermission(PERMISSIONS.DASHBOARD_VIEW)
    ) {
      const landing = resolveDefaultLandingRoute((perm) => permissionsStore.hasPermission(perm))
      if (landing !== to.path) {
        next({ path: landing, replace: true })
        return
      }
    }
  }

  if (to.meta.permission) {
    const requiredPermission = to.meta.permission as string

    if (!authStore.isSuperAdmin && !permissionsStore.hasPermission(requiredPermission)) {
      notifyRouteForbidden()
      next({ path: '/forbidden', query: { from: to.fullPath } })
      return
    }
  }

  next()
}

export const guestGuard = async (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const authStore = useAuthStore()

  if (!authStore.bootstrapped) {
    await authStore.bootstrap()
  }

  if (authStore.isAuthenticated) {
    if (isTokenBasedGuestRoute(to.path, (to.query ?? {}) as Record<string, unknown>)) {
      next()
      return
    }

    // A signed-in but still-unverified user must reach /auth/verify-email, otherwise the
    // "Resend verification email" action lives on a page they are redirected away from (A1-07).
    if (to.path === '/auth/verify-email' && authStore.user?.email_verified !== true) {
      next()
      return
    }

    if (
      to.path === '/login' ||
      to.path === '/auth/login' ||
      to.path === '/register' ||
      to.path === '/auth/register' ||
      to.path === '/auth/forgot-password' ||
      to.path === '/auth/reset-password' ||
      to.path === '/auth/accept-invite' ||
      to.path === '/auth/verify-email'
    ) {
      const permissionsStore = usePermissionsStore()
      if (permissionsStore.permissions.length === 0 && !permissionsStore.loading) {
        await permissionsStore.fetchPermissions({ force: true })
      }
      next(resolveDefaultLandingRoute((perm) => permissionsStore.hasPermission(perm)))
      return
    }
  }

  next()
}
